// src/pages/public/About.js
import React from "react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { COMPANY } from "../../utils/constants";

const TEAM = [
  { id: "ailen", name: "Ailen", role: "Founder & Lead Developer", bio: "10+ years of IT experience in web systems, database architecture, and automation.", initials: "AI", photo: "/ailen.png"},
  { id: "aaron", name: "Aaron", role: "Full-Stack Developer", bio: "Designs responsive UIs and builds scalable backend systems for high-performance web apps.", initials: "AA", photo:"/boy.png" },
  { id: "abie", name: "Abie", role: "UI/UX Designer", bio: "Creates intuitive user experiences and accessible designs for web and mobile platforms.", initials: "AB", photo:"/boy.png" },
  { id: "jerwin", name: "Jerwin", role: "QA & Frontend Developer", bio: "Pixel-perfect implementation of modern UI components using React and modern CSS.", initials: "JE", photo:"/boy.png" },
  { id: "pau", name: "Pau", role: "Customer Support Representative", bio: "Delivered exceptional phone-based support for U.S. customers. Handled billing, account resolution, and product inquiries.", initials: "FR", photo:"/girl.png" },
];

export default function About() {
  return (
    <div style={{ paddingTop: "var(--header-h)" }}>
      {/* Hero */}
      <Helmet>
        <title>About Us - Aimble I.T. Solutions</title>
        <meta name="description" content="Learn about Aimble I.T. Solutions, a registered web development and IT company in Irosin, Sorsogon. We build fast, secure websites and digital solutions." />
        <link rel="canonical" href="https://www.aimbletech.com/about" />
      </Helmet>
      <section className="section">
        <div className="container about-hero">
          
          {/* Left Column: Bold Statement */}
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }}>
            <div className="section-label">About Us</div>
            <h1 className="display" style={{ marginBottom: 24 }}>
              Philippine-born.<br/>
              <span style={{ color: "var(--accent)" }}>World-ready.</span>
            </h1>
          </motion.div>

          {/* Right Column: Supporting Text & Badges */}
          <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}>
            <p className="body-lg text-muted" style={{ marginBottom: 24 }}>
              Aimble I.T. Solutions is a team of passionate developers, designers, and IT professionals based in Irosin, Sorsogon. We specialize in building custom digital solutions for businesses of all sizes.
            </p>
            <div className="about-badges" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div className="flex-col gap-sm">
              <span className="badge badge-blue">✓ DTI Business Name Registration</span>
              <span className="badge badge-amber">✓ Mayor's Permit</span>
              {/* <span className="badge badge-accent">✓ BIR Certificate of Registration</span> */}
              <img 
                  src="/BIR_BADGE.png" 
                  alt="BIR Registered Business" 
                  style={{ height: 150, width: 1000, objectFit: "contain", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.1))" }} 
                />      
            </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Values */}
      <section className="section-sm" style={{ background: "var(--surface)" }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 40 }}>
            <div className="section-label">Our Values</div>
            <h2 className="heading-1">Why clients choose Aimble</h2>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
            {[
              { icon: "🔐", title: "Trustworthy", desc: "Government-registered. Official receipts issued. No hidden fees." },
              { icon: "⚡", title: "Fast Delivery", desc: "We respect deadlines and communicate proactively throughout your project." },
              { icon: "📱", title: "Mobile-First", desc: "Every website we build works beautifully on mobile — especially on Philippine mobile networks." },
              { icon: "🌍", title: "Global Reach", desc: "We serve international clients remotely. Time zone differences are no problem." },
              { icon: "🔧", title: "Long-Term Support", desc: "We don't disappear after launch. Maintenance plans keep your site running smoothly." },
              { icon: "💡", title: "Future-Ready", desc: "Built on Firebase and modern frameworks — your website scales with your business." },
            ].map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}>
                <div className="card">
                  <div style={{ fontSize: 28, marginBottom: 12 }}>{v.icon}</div>
                  <h3 style={{ fontSize: "0.9375rem", fontWeight: 600, marginBottom: 6 }}>{v.title}</h3>
                  <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.6 }}>{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 48 }}>
            <div className="section-label">The Team</div>
            <h2 className="heading-1">Meet the people behind Aimble</h2>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 24 }}>
            {TEAM.map((member, i) => (
              <motion.div key={member.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="card" style={{ textAlign: "center", display: "flex", flexDirection: "column", height: "100%" }}>
                  
                  <img 
                    src={member.photo} 
                    alt={member.name} 
                    style={{
                      width: 80, 
                      height: 80,
                      borderRadius: "50%",
                      objectFit: "cover",
                      margin: "0 auto 16px",
                      border: "2px solid var(--surface-2)",
                      backgroundColor: "var(--navy-mid)" 
                    }} 
                    onError={(e) => {
                      e.target.onerror = null; 
                      e.target.src = "https://ui-avatars.com/api/?name=" + member.name + "&background=0D1B2A&color=00C9A7";
                    }}
                  />

                  <h3 style={{ fontSize: "1rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 4 }}>{member.name}</h3>
                  <div style={{ fontSize: "0.8125rem", color: "var(--accent)", fontWeight: 500, marginBottom: 10 }}>{member.role}</div>
                  <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.6, flex: 1 }}>{member.bio}</p>
                  
                  <Link 
                    to={`/team/${member.id}`} 
                    className="btn btn-secondary btn-sm" 
                    style={{ marginTop: 16, width: "100%", justifyContent: "center" }}
                  >
                    View CV →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-sm" style={{ background: "var(--surface)" }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="about-location">
              <div>
                <div className="section-label">Find Us</div>
                <h2 className="heading-1" style={{ marginBottom: 16 }}>Visit our shop</h2>
                <p style={{ color: "var(--text-muted)", marginBottom: 24, lineHeight: 1.7 }}>
                  Our physical location serves the Irosin and surrounding Sorsogon communities with walk-in IT services. We're open for printing, PC rental, tech merchandise, and consultations.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>📍</span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Address</div>
                      <div style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>{COMPANY.address}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>✉️</span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Email</div>
                      <a href={`mailto:${COMPANY.email}`} style={{ color: "var(--accent)", fontSize: "0.875rem" }}>{COMPANY.email}</a>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>📘</span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>Facebook</div>
                      <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)", fontSize: "0.875rem" }}>@aimbleph</a>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ background: "var(--navy-mid)", borderRadius: 16, overflow: "hidden", height: 300, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--border-med)", fontSize: "0.875rem", color: "var(--text-muted)", textAlign: "center", padding: 24 }}>
                <div>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>🗺️</div>
                  <p>Purok 1, Tabon-tabon<br />Irosin, Sorsogon</p>
                  <a href="https://maps.google.com/?q=Irosin+Sorsogon+Philippines" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ marginTop: 16 }}>
                    Open in Google Maps →
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-sm">
        <div className="container" style={{ textAlign: "center" }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="heading-1" style={{ marginBottom: 16 }}>Let's work together</h2>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/packages" className="btn btn-primary btn-lg">See Our Packages</Link>
              <Link to="/contact" className="btn btn-secondary btn-lg">Get In Touch</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MOBILE RESPONSIVE CSS */}
      <style>{`
        .about-hero {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
        }
        .about-location {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
        }
        @media(max-width: 992px) {
          .about-hero {
            grid-template-columns: 1fr;
            gap: 40px;
            text-align: center;
          }
          .about-hero > div {
            margin: 0 auto;
          }
          .about-badges {
            justify-content: center;
          }
        }
        @media(max-width: 768px) {
          .about-location {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}